import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  mcpApi,
  type McpCatalogueItem,
  type McpHttpConfig,
  type McpStdioConfig,
  type McpAuthConfig,
  type McpSecretScope,
} from "@/api/mcp";
import {
  Globe, Terminal, Radio, Shield, Eye, EyeOff, RefreshCw, Lock, Building2,
  CheckCircle2, XCircle, Loader2,
} from "lucide-react";

type TransportType = "http" | "sse" | "stdio";
type AuthType = "none" | "api_key" | "bearer" | "oauth_client_credentials" | "oauth_signin";

export interface McpEditorScope {
  kind: "platform" | "tenant";
  // Required when kind === "tenant" — the MCP will be owned by this tenant.
  // When kind === "platform" and tenantId is set, a "tenant-owned" MCP is
  // being created/edited from the admin UI on behalf of that tenant.
  tenantId?: string | null;
}

interface McpEditorProps {
  scope: McpEditorScope;
  initialValue?: McpCatalogueItem;
  onSaved: (row: McpCatalogueItem) => void;
  onCancel: () => void;
  // Optional platform-admin tenant picker: when provided and scope.kind ===
  // "platform", the editor shows a "Assign to tenant" selector that lets the
  // admin choose ownership (null = platform-wide).
  tenantOptions?: Array<{ id: string; name: string }>;
}

interface McpForm {
  name: string;
  displayName: string;
  description: string;
  transportType: TransportType;
  secretScope: McpSecretScope;
  companyId: string | null;
  // Platform-managed only — ignored when companyId is set.
  isAvailableToAll: boolean;
  allocatedCompanyIds: string[];
  url: string;
  headers: string;
  authType: AuthType;
  apiKeyHeader: string;
  apiKeyValue: string;
  bearerToken: string;
  oauthTokenUrl: string;
  oauthClientId: string;
  oauthClientSecret: string;
  oauthScope: string;
  oauthSigninScope: string;
  command: string;
  args: string;
  env: string;
}

const EMPTY_FORM: McpForm = {
  name: "",
  displayName: "",
  description: "",
  transportType: "http",
  secretScope: "global",
  companyId: null,
  // New platform MCPs start "available to all" — matches the legacy default
  // so admins don't have to remember to flip a switch on every create.
  isAvailableToAll: true,
  allocatedCompanyIds: [],
  url: "",
  headers: "",
  authType: "none" as AuthType,
  apiKeyHeader: "x-api-key",
  apiKeyValue: "",
  bearerToken: "",
  oauthTokenUrl: "",
  oauthClientId: "",
  oauthClientSecret: "",
  oauthScope: "",
  oauthSigninScope: "",
  command: "",
  args: "",
  env: "",
};

function itemToForm(item: McpCatalogueItem): McpForm {
  const http = item.httpConfig;
  const stdio = item.stdioConfig;
  const auth = http?.auth;
  return {
    name: item.name,
    displayName: item.displayName || "",
    description: item.description || "",
    transportType: item.transportType,
    secretScope: item.secretScope || "global",
    companyId: item.companyId,
    isAvailableToAll: item.isAvailableToAll ?? false,
    allocatedCompanyIds: item.allocatedCompanyIds ?? [],
    url: http?.url || "",
    headers: http?.headers ? JSON.stringify(http.headers, null, 2) : "",
    authType: auth?.type || "none",
    apiKeyHeader: auth?.apiKeyHeader || "x-api-key",
    apiKeyValue: auth?.apiKeyValue || "",
    bearerToken: auth?.bearerToken || "",
    oauthTokenUrl: auth?.oauthTokenUrl || "",
    oauthClientId: auth?.oauthClientId || "",
    oauthClientSecret: auth?.oauthClientSecret || "",
    oauthScope: auth?.oauthScope || "",
    oauthSigninScope: (auth as { oauthSigninScope?: string })?.oauthSigninScope || "",
    command: stdio?.command || "",
    args: stdio?.args?.join(" ") || "",
    env: stdio?.env ? JSON.stringify(stdio.env, null, 2) : "",
  };
}

function formToPayload(form: McpForm): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    name: form.name,
    displayName: form.displayName || form.name,
    description: form.description || undefined,
    transportType: form.transportType,
    secretScope: form.secretScope,
    companyId: form.companyId,
  };

  // Availability fields only apply to platform-managed rows (companyId =
  // null). Tenant-owned MCPs are scoped by ownership already.
  if (form.companyId === null) {
    payload.isAvailableToAll = form.isAvailableToAll;
    payload.allocatedCompanyIds = form.isAvailableToAll ? [] : form.allocatedCompanyIds;
  }

  if (form.transportType === "http" || form.transportType === "sse") {
    const httpConfig: McpHttpConfig = { url: form.url };
    if (form.headers.trim()) {
      try { httpConfig.headers = JSON.parse(form.headers); } catch { /* ignore */ }
    }
    const auth: McpAuthConfig = { type: form.authType };
    if (form.authType === "api_key") {
      auth.apiKeyHeader = form.apiKeyHeader || "x-api-key";
      auth.apiKeyValue = form.apiKeyValue;
    } else if (form.authType === "bearer") {
      auth.bearerToken = form.bearerToken;
    } else if (form.authType === "oauth_client_credentials") {
      auth.oauthTokenUrl = form.oauthTokenUrl;
      auth.oauthClientId = form.oauthClientId;
      auth.oauthClientSecret = form.oauthClientSecret;
      auth.oauthScope = form.oauthScope || undefined;
    } else if (form.authType === "oauth_signin") {
      // No secrets stored on the config — the live token state lives in the
      // mcp_oauth_connections table after the admin signs in. Only an
      // optional scope override travels with the config.
      (auth as { oauthSigninScope?: string }).oauthSigninScope =
        form.oauthSigninScope || undefined;
    }
    if (form.authType !== "none") httpConfig.auth = auth;
    payload.httpConfig = httpConfig;
  } else if (form.transportType === "stdio") {
    const stdioConfig: McpStdioConfig = { command: form.command };
    if (form.args.trim()) stdioConfig.args = form.args.split(/\s+/).filter(Boolean);
    if (form.env.trim()) {
      try { stdioConfig.env = JSON.parse(form.env); } catch { /* ignore */ }
    }
    payload.stdioConfig = stdioConfig;
  }
  return payload;
}

const transportIcons: Record<TransportType, typeof Globe> = { http: Globe, sse: Radio, stdio: Terminal };
const transportLabels: Record<TransportType, string> = {
  http: "HTTP (Streamable)",
  sse: "SSE (Server-Sent Events)",
  stdio: "Stdio (Local Process)",
};
const authLabels: Record<AuthType, string> = {
  none: "No Authentication",
  api_key: "API Key",
  bearer: "Bearer Token",
  oauth_client_credentials: "OAuth 2.0 Client Credentials",
  oauth_signin: "OAuth 2.0 (Sign in)",
};

export function McpEditor({ scope, initialValue, onSaved, onCancel, tenantOptions }: McpEditorProps) {
  // Tenant-scope editor forces the secretScope to "tenant" (secrets go to
  // the owning tenant's Key Vault). When editing a tenant-owned row from
  // the platform page, the same rule applies via initialValue.companyId.
  const initialForm: McpForm = (() => {
    if (initialValue) return itemToForm(initialValue);
    if (scope.kind === "tenant" && scope.tenantId) {
      return { ...EMPTY_FORM, companyId: scope.tenantId, secretScope: "tenant" };
    }
    return EMPTY_FORM;
  })();

  const [form, setForm] = useState<McpForm>(initialForm);
  const [showSecrets, setShowSecrets] = useState(false);

  const isTenantOwned = form.companyId !== null;
  const lockSecretScope = isTenantOwned; // tenant-owned MCPs must use tenant vault
  const showTenantPicker = scope.kind === "platform" && !initialValue && Array.isArray(tenantOptions);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = formToPayload({
        ...form,
        secretScope: lockSecretScope ? "tenant" : form.secretScope,
      });
      if (initialValue) {
        // Scope (companyId) is immutable server-side; don't send it on update.
        delete (payload as Record<string, unknown>).companyId;
        return mcpApi.updateCatalogue(initialValue.id, payload);
      }
      return mcpApi.createCatalogue(payload);
    },
    onSuccess: (row) => onSaved(row),
  });

  const testFormMutation = useMutation({
    mutationFn: async () => {
      const payload = formToPayload(form);
      const http = payload.httpConfig as McpHttpConfig | undefined;
      return mcpApi.test({
        mcpId: initialValue?.id,
        transportType: form.transportType,
        url: http?.url,
        headers: http?.headers,
        auth: http?.auth,
      });
    },
  });

  // --- OAuth Sign-In state ---
  const oauthEnabled = form.authType === "oauth_signin" && !!initialValue?.id;
  const oauthStatusQuery = useQuery({
    queryKey: ["mcp-oauth-status", initialValue?.id],
    queryFn: () => mcpApi.oauthStatus(initialValue!.id),
    enabled: oauthEnabled,
    refetchInterval: (q) => (q.state.data?.status === "pending" ? 2500 : false),
  });
  const [signingIn, setSigningIn] = useState(false);
  const [oauthError, setOauthError] = useState<string | null>(null);
  const [oauthLoginHint, setOauthLoginHint] = useState("");

  const signIn = async () => {
    if (!initialValue?.id) return;
    setOauthError(null);
    setSigningIn(true);
    try {
      const { authorizeUrl } = await mcpApi.oauthStart(initialValue.id, {
        scope: form.oauthSigninScope || undefined,
        loginHint: oauthLoginHint.trim() || undefined,
      });
      const popup = window.open(authorizeUrl, "mcp-oauth", "width=520,height=720");
      // Refresh status when the callback posts back (or the popup closes).
      const onMsg = (ev: MessageEvent) => {
        if (ev.data?.type === "mcp-oauth") {
          window.removeEventListener("message", onMsg);
          setSigningIn(false);
          oauthStatusQuery.refetch();
        }
      };
      window.addEventListener("message", onMsg);
      const poll = setInterval(() => {
        if (popup?.closed) {
          clearInterval(poll);
          window.removeEventListener("message", onMsg);
          setSigningIn(false);
          oauthStatusQuery.refetch();
        }
      }, 800);
    } catch (e) {
      setOauthError(e instanceof Error ? e.message : String(e));
      setSigningIn(false);
    }
  };

  const disconnectOAuth = async () => {
    if (!initialValue?.id) return;
    await mcpApi.oauthDisconnect(initialValue.id);
    oauthStatusQuery.refetch();
  };

  const isRemote = form.transportType === "http" || form.transportType === "sse";
  const canTest = isRemote && form.url.trim().length > 0;
  const canSave = form.name.trim().length > 0 &&
    (isRemote ? form.url.trim().length > 0 : form.command.trim().length > 0);

  return (
    <Card>
      <CardContent className="space-y-4 pt-4">
        {/* Basic info */}
        <div className="space-y-3">
          <h3 className="text-sm font-medium text-text-primary">Server Details</h3>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-xs text-text-secondary">Name *</label>
              <Input
                placeholder="e.g. github-mcp"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>
            <div>
              <label className="mb-1 block text-xs text-text-secondary">Display Name</label>
              <Input
                placeholder="e.g. GitHub"
                value={form.displayName}
                onChange={(e) => setForm({ ...form, displayName: e.target.value })}
              />
            </div>
          </div>
          <div>
            <label className="mb-1 block text-xs text-text-secondary">Description</label>
            <Input
              placeholder="What does this MCP server provide?"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
          </div>
        </div>

        {/* Scope / ownership — only visible to platform admin when creating */}
        {showTenantPicker && (
          <div className="space-y-2">
            <h3 className="text-sm font-medium text-text-primary">Scope</h3>
            <p className="text-xs text-text-muted">
              Platform-managed MCPs live under the catalogue and can be allocated to
              specific tenants. Tenant-scoped MCPs live under a specific tenant and use
              that tenant's Key Vault.
            </p>
            <select
              value={form.companyId ?? ""}
              onChange={(e) => setForm({ ...form, companyId: e.target.value || null })}
              className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm"
            >
              <option value="">Platform (managed centrally)</option>
              {tenantOptions!.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>
        )}

        {/* Availability — platform-managed MCPs only. Controls which tenants
            see the MCP. Hidden for tenant-owned rows (their scope is set by
            ownership). */}
        {scope.kind === "platform" && form.companyId === null && Array.isArray(tenantOptions) && (
          <div className="space-y-3">
            <h3 className="text-sm font-medium text-text-primary">Availability</h3>
            <label className="flex items-start gap-2 rounded-lg border border-border bg-surface p-3 cursor-pointer">
              <input
                type="checkbox"
                checked={form.isAvailableToAll}
                onChange={(e) => setForm({ ...form, isAvailableToAll: e.target.checked })}
                className="mt-0.5"
              />
              <div>
                <p className="text-xs font-medium text-text-primary">Available to all tenants</p>
                <p className="text-[11px] text-text-muted">
                  Every tenant sees this MCP in their settings. Uncheck to restrict to specific tenants.
                </p>
              </div>
            </label>
            {!form.isAvailableToAll && (
              <div className="rounded-lg border border-border bg-surface p-3 space-y-2">
                <p className="text-xs font-medium text-text-primary">Allocated tenants</p>
                <p className="text-[11px] text-text-muted">Only the selected tenants will see this MCP.</p>
                <div className="max-h-48 overflow-y-auto flex flex-col gap-1 pr-1">
                  {tenantOptions.map((t) => {
                    const checked = form.allocatedCompanyIds.includes(t.id);
                    return (
                      <label key={t.id} className="flex items-center gap-2 text-xs text-text-secondary cursor-pointer hover:text-text-primary">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => {
                            const next = e.target.checked
                              ? [...form.allocatedCompanyIds, t.id]
                              : form.allocatedCompanyIds.filter((id) => id !== t.id);
                            setForm({ ...form, allocatedCompanyIds: next });
                          }}
                        />
                        {t.name}
                      </label>
                    );
                  })}
                  {tenantOptions.length === 0 && (
                    <p className="text-[11px] text-text-muted">No tenants available.</p>
                  )}
                </div>
                {form.allocatedCompanyIds.length === 0 && (
                  <p className="text-[11px] text-amber-600">
                    No tenants selected — this MCP will be hidden from everyone.
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Transport type */}
        <div className="space-y-3">
          <h3 className="text-sm font-medium text-text-primary">Transport</h3>
          <div className="grid grid-cols-3 gap-2">
            {(["http", "sse", "stdio"] as TransportType[]).map((t) => {
              const Icon = transportIcons[t];
              const active = form.transportType === t;
              return (
                <button
                  key={t}
                  onClick={() => setForm({ ...form, transportType: t })}
                  className={`flex items-center gap-2 rounded-lg border p-3 text-left transition-colors ${
                    active
                      ? "border-accent bg-accent/5 text-accent"
                      : "border-border bg-surface text-text-secondary hover:border-text-muted"
                  }`}
                >
                  <Icon size={16} />
                  <div>
                    <p className="text-xs font-medium">{t.toUpperCase()}</p>
                    <p className="text-[10px] opacity-70">
                      {transportLabels[t].split("(")[1]?.replace(")", "") || "Remote"}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Secret Scope (hidden when tenant-owned — it's forced to tenant) */}
        {!lockSecretScope && (
          <div className="space-y-3">
            <h3 className="text-sm font-medium text-text-primary">Secret Storage</h3>
            <p className="text-xs text-text-muted">
              Controls where authentication credentials are stored in Azure Key Vault.
            </p>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => setForm({ ...form, secretScope: "global" })}
                className={`flex items-center gap-2 rounded-lg border p-3 text-left transition-colors ${
                  form.secretScope === "global"
                    ? "border-accent bg-accent/5 text-accent"
                    : "border-border bg-surface text-text-secondary hover:border-text-muted"
                }`}
              >
                <Lock size={16} />
                <div>
                  <p className="text-xs font-medium">Global</p>
                  <p className="text-[10px] opacity-70">Platform Key Vault (shared)</p>
                </div>
              </button>
              <button
                onClick={() => setForm({ ...form, secretScope: "tenant" })}
                className={`flex items-center gap-2 rounded-lg border p-3 text-left transition-colors ${
                  form.secretScope === "tenant"
                    ? "border-accent bg-accent/5 text-accent"
                    : "border-border bg-surface text-text-secondary hover:border-text-muted"
                }`}
              >
                <Building2 size={16} />
                <div>
                  <p className="text-xs font-medium">Tenant-Scoped</p>
                  <p className="text-[10px] opacity-70">Per-tenant Key Vault (isolated)</p>
                </div>
              </button>
            </div>
          </div>
        )}

        {/* HTTP / SSE config */}
        {isRemote && (
          <div className="space-y-3">
            <h3 className="text-sm font-medium text-text-primary">Connection</h3>
            <div>
              <label className="mb-1 block text-xs text-text-secondary">URL *</label>
              <Input
                placeholder={form.transportType === "sse" ? "https://example.com/sse" : "https://example.com/mcp"}
                value={form.url}
                onChange={(e) => setForm({ ...form, url: e.target.value })}
              />
            </div>
            <div>
              <label className="mb-1 block text-xs text-text-secondary">Custom Headers (JSON, optional)</label>
              <textarea
                placeholder='{"X-Custom": "value"}'
                value={form.headers}
                onChange={(e) => setForm({ ...form, headers: e.target.value })}
                rows={2}
                className="w-full rounded-md border border-border bg-surface px-3 py-2 font-mono text-xs text-text-primary"
              />
            </div>

            <div className="space-y-3 rounded-lg border border-border p-3">
              <div className="flex items-center justify-between">
                <h4 className="flex items-center gap-1.5 text-sm font-medium text-text-primary">
                  <Shield size={14} />
                  Authentication
                </h4>
                <button
                  onClick={() => setShowSecrets(!showSecrets)}
                  className="flex items-center gap-1 text-xs text-text-muted hover:text-text-secondary"
                >
                  {showSecrets ? <EyeOff size={12} /> : <Eye size={12} />}
                  {showSecrets ? "Hide" : "Show"} secrets
                </button>
              </div>
              <select
                value={form.authType}
                onChange={(e) => setForm({ ...form, authType: e.target.value as AuthType })}
                className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm"
              >
                {(Object.keys(authLabels) as AuthType[]).map((a) => (
                  <option key={a} value={a}>{authLabels[a]}</option>
                ))}
              </select>

              {form.authType === "api_key" && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="mb-1 block text-xs text-text-secondary">Header Name</label>
                    <Input
                      placeholder="x-api-key"
                      value={form.apiKeyHeader}
                      onChange={(e) => setForm({ ...form, apiKeyHeader: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs text-text-secondary">API Key</label>
                    <Input
                      type={showSecrets ? "text" : "password"}
                      placeholder="sk-..."
                      value={form.apiKeyValue}
                      onChange={(e) => setForm({ ...form, apiKeyValue: e.target.value })}
                    />
                  </div>
                </div>
              )}

              {form.authType === "bearer" && (
                <div>
                  <label className="mb-1 block text-xs text-text-secondary">Bearer Token</label>
                  <Input
                    type={showSecrets ? "text" : "password"}
                    placeholder="eyJ..."
                    value={form.bearerToken}
                    onChange={(e) => setForm({ ...form, bearerToken: e.target.value })}
                  />
                </div>
              )}

              {form.authType === "oauth_client_credentials" && (
                <div className="space-y-3">
                  <div>
                    <label className="mb-1 block text-xs text-text-secondary">Token URL *</label>
                    <Input
                      placeholder="https://login.microsoftonline.com/.../oauth2/v2.0/token"
                      value={form.oauthTokenUrl}
                      onChange={(e) => setForm({ ...form, oauthTokenUrl: e.target.value })}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="mb-1 block text-xs text-text-secondary">Client ID *</label>
                      <Input
                        placeholder="client-id"
                        value={form.oauthClientId}
                        onChange={(e) => setForm({ ...form, oauthClientId: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs text-text-secondary">Client Secret *</label>
                      <Input
                        type={showSecrets ? "text" : "password"}
                        placeholder="client-secret"
                        value={form.oauthClientSecret}
                        onChange={(e) => setForm({ ...form, oauthClientSecret: e.target.value })}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="mb-1 block text-xs text-text-secondary">Scope (optional)</label>
                    <Input
                      placeholder="https://graph.microsoft.com/.default"
                      value={form.oauthScope}
                      onChange={(e) => setForm({ ...form, oauthScope: e.target.value })}
                    />
                  </div>
                </div>
              )}

              {form.authType === "oauth_signin" && (
                <div className="space-y-3">
                  <p className="text-xs text-text-muted">
                    The MCP server runs its own OAuth (discovery + dynamic client registration
                    + PKCE). Sign in once with your Microsoft account; ProClaw stores the token
                    and refreshes it automatically, then vends a fresh bearer to the agent. This
                    is fully isolated from the tenant M365 service identity used for mailbox/Teams.
                  </p>
                  <div>
                    <label className="mb-1 block text-xs text-text-secondary">Scope override (optional)</label>
                    <Input
                      placeholder="leave blank to use the server's advertised scopes"
                      value={form.oauthSigninScope}
                      onChange={(e) => setForm({ ...form, oauthSigninScope: e.target.value })}
                    />
                  </div>

                  {!initialValue?.id ? (
                    <div className="rounded-md border border-amber-500/20 bg-amber-500/5 px-3 py-2 text-xs text-amber-500">
                      Save the MCP server first, then re-open it to sign in.
                    </div>
                  ) : (
                    <div className="space-y-2 rounded-md border border-border bg-surface-hover p-3">
                      <div>
                        <label className="mb-1 block text-xs text-text-secondary">
                          Sign in as (optional)
                        </label>
                        <Input
                          placeholder="account@aztechit.co.uk — leave blank to pick on the Microsoft screen"
                          value={oauthLoginHint}
                          onChange={(e) => setOauthLoginHint(e.target.value)}
                        />
                        <p className="mt-1 text-[11px] text-text-muted">
                          You'll always get the Microsoft account chooser; this just pre-fills it.
                        </p>
                      </div>
                      {(() => {
                        const st = oauthStatusQuery.data;
                        const connected = st?.status === "connected";
                        return (
                          <>
                            <div className="flex items-center justify-between gap-2">
                              <div className="flex items-center gap-2 text-sm">
                                {connected ? (
                                  <CheckCircle2 size={15} className="text-green-500" />
                                ) : st?.status === "error" ? (
                                  <XCircle size={15} className="text-red-500" />
                                ) : (
                                  <Lock size={15} className="text-text-muted" />
                                )}
                                <span className="text-text-primary">
                                  {connected
                                    ? `Connected${st?.signedInAs ? ` as ${st.signedInAs}` : ""}`
                                    : st?.status === "error"
                                      ? "Connection error"
                                      : "Not connected"}
                                </span>
                              </div>
                              <div className="flex gap-2">
                                <Button size="sm" variant="outline" disabled={signingIn} onClick={signIn}>
                                  {signingIn ? (
                                    <Loader2 size={13} className="mr-1.5 animate-spin" />
                                  ) : null}
                                  {connected ? "Re-authenticate" : "Sign in with Microsoft"}
                                </Button>
                                {connected && (
                                  <Button size="sm" variant="ghost" onClick={disconnectOAuth}>
                                    Disconnect
                                  </Button>
                                )}
                              </div>
                            </div>
                            {connected && st?.expiresAt && (
                              <p className="text-xs text-text-muted">
                                Token expires {new Date(st.expiresAt).toLocaleString()} (auto-refreshed).
                              </p>
                            )}
                            {st?.status === "error" && st?.lastError && (
                              <p className="text-xs text-red-400">{st.lastError}</p>
                            )}
                            {oauthError && <p className="text-xs text-red-400">{oauthError}</p>}
                          </>
                        );
                      })()}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Stdio config */}
        {form.transportType === "stdio" && (
          <div className="space-y-3">
            <h3 className="text-sm font-medium text-text-primary">Process</h3>
            <div>
              <label className="mb-1 block text-xs text-text-secondary">Command *</label>
              <Input
                placeholder="e.g. npx, python, uvx"
                value={form.command}
                onChange={(e) => setForm({ ...form, command: e.target.value })}
              />
            </div>
            <div>
              <label className="mb-1 block text-xs text-text-secondary">Arguments (space-separated)</label>
              <Input
                placeholder="e.g. -m mcp_server --port 8080"
                value={form.args}
                onChange={(e) => setForm({ ...form, args: e.target.value })}
              />
            </div>
            <div>
              <label className="mb-1 block text-xs text-text-secondary">Environment Variables (JSON, optional)</label>
              <textarea
                placeholder='{"GITHUB_TOKEN": "ghp_...", "DEBUG": "true"}'
                value={form.env}
                onChange={(e) => setForm({ ...form, env: e.target.value })}
                rows={2}
                className="w-full rounded-md border border-border bg-surface px-3 py-2 font-mono text-xs text-text-primary"
              />
            </div>
          </div>
        )}

        {/* Test connection */}
        {isRemote && (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                disabled={!canTest || testFormMutation.isPending}
                onClick={() => testFormMutation.mutate()}
              >
                {testFormMutation.isPending ? (
                  <Loader2 size={14} className="mr-1.5 animate-spin" />
                ) : (
                  <RefreshCw size={14} className="mr-1.5" />
                )}
                Test Connection
              </Button>
              {testFormMutation.data && (
                <span className={`flex items-center gap-1 text-xs ${testFormMutation.data.success ? "text-green-500" : "text-red-500"}`}>
                  {testFormMutation.data.success ? <CheckCircle2 size={14} /> : <XCircle size={14} />}
                  {testFormMutation.data.success
                    ? `Connected - ${testFormMutation.data.tools?.length || 0} tools discovered`
                    : testFormMutation.data.error}
                </span>
              )}
            </div>
            {testFormMutation.data?.success && testFormMutation.data.tools && testFormMutation.data.tools.length > 0 && (
              <div className="rounded-lg border border-green-500/20 bg-green-500/5 p-3">
                <p className="mb-2 text-xs font-medium text-green-600">Discovered Tools:</p>
                <div className="flex flex-wrap gap-1.5">
                  {testFormMutation.data.tools.map((tool) => (
                    <span
                      key={tool.name}
                      className="rounded bg-green-500/10 px-2 py-0.5 text-[11px] font-mono text-green-700"
                      title={tool.description}
                    >
                      {tool.name}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center gap-2 border-t border-border pt-4">
          <Button
            disabled={!canSave || saveMutation.isPending}
            onClick={() => saveMutation.mutate()}
          >
            {saveMutation.isPending && <Loader2 size={14} className="mr-1.5 animate-spin" />}
            {initialValue ? "Save Changes" : "Create MCP Server"}
          </Button>
          <Button variant="ghost" onClick={onCancel}>Cancel</Button>
          {saveMutation.isError && (
            <span className="text-xs text-red-500">
              {(saveMutation.error as Error)?.message || "Failed to save"}
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
